/**
 * Platform buckets for the manual "add to home screen / install" fallback shown
 * on the install-required screen when the browser never fired
 * `beforeinstallprompt` (iOS Safari, Firefox, or a Chromium that already
 * consumed its one-shot prompt). Each bucket maps to a distinct set of steps in
 * `InstallRequiredComponent`.
 */
export type InstallPlatform = 'ios' | 'android' | 'desktop' | 'firefox' | 'safari' | 'other';

/**
 * Classifies a user-agent into the manual-install bucket whose steps apply:
 *   - `ios`     — iPhone/iPad (all iOS browsers install via Safari's Share sheet).
 *   - `android` — Chromium on Android (browser menu → Install / Add to Home screen).
 *   - `desktop` — Chromium desktop (install icon in the address bar).
 *   - `firefox` — Firefox, which can't install/open a PWA → direct to Chrome/Edge.
 *   - `safari`  — Safari on macOS (Add to Dock, then open from the Dock).
 *   - `other`   — anything else → direct to Chrome/Edge.
 * Order matters: iOS first (all iOS is WebKit), then Firefox and Chromium
 * (which also send "Safari" tokens), then desktop Safari. Pure for easy testing.
 */
export function detectInstallPlatform(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): InstallPlatform {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Firefox\/|FxiOS/i.test(ua)) return 'firefox';
  if (/Chrome\/|Chromium\/|Edg\//i.test(ua)) return /Android|Mobile/i.test(ua) ? 'android' : 'desktop';
  if (/Safari\//i.test(ua) && /Macintosh|Mac OS X/i.test(ua)) return 'safari';
  return 'other';
}
